import { useState } from 'react';
import { Icon, IconName } from '@ardoq/icons';
import { WithPopover } from '@ardoq/popovers';
import {
  InputsSection,
  PanelContainer,
  PanelFooter,
  PanelHeader,
  SectionsContainer,
  StyledField,
} from './atoms';
import { Button, ButtonType, PrimaryButton, GhostButton } from '@ardoq/button';
import { ReadOnlyStyle } from '@ardoq/forms';
import { connect, dispatchAction, isActionOfType } from '@ardoq/rxbeach';
import { hideViewSidePanel } from 'views/mainApp/viewSidePanel/actions';
import { TextAreaWithCounter } from '@ardoq/snowflakes';
import { useEffectOnce } from '@ardoq/hooks';
import { saveAsViewpoint$ } from './saveAsViewpoint$/saveAsViewpoint$';
import { SaveAsViewpointState } from './saveAsViewpoint$/types';
import {
  resetSaveAsViewpoint,
  updateSaveAsViewpointState,
} from './saveAsViewpoint$/actions';
import { editorOpened } from 'perspective/actions';
import { AppliedSettings } from './appliedSettings/AppliedSettings';
import { combineLatest, map } from 'rxjs';
import { traversalState$ } from 'streams/traversals$';
import { loadedState$ } from 'loadedState/loadedState$';
import { isSingleTraversalState } from './saveAsViewpoint$/isSingleTraversalState';
import { loadedStateOperations } from 'loadedState/loadedStateOperations';
import { LocationSelect } from 'components/LocationSelect/LocationSelect';
import { moveAssets } from 'streams/assetFolders/actions';
import {
  createViewpoint,
  createViewpointFailure,
  createViewpointSuccess,
  updateViewpoint,
  updateViewpointFailure,
  updateViewpointSuccess,
} from 'viewpointBuilder/actions';
import { setTraversalId } from 'loadedState/actions';
import { dispatchActionAndWaitForResponse } from 'actions/utils';
import {
  APIViewpointAttributes,
  ArdoqId,
  AssetType,
  LoadedState,
  ViewIds,
} from '@ardoq/api-types';
import { toUpdatedViewpointViewId } from 'views/environmentals/getViewpointModeSupportedViews';
import { getViewSettingsToSave } from './utils';
import { getSaveViewpointTrackingData } from './getSaveViewpointTrackingData';
import { trackSavingViewpoint } from './tracking';
import { activeViewState$ } from 'streams/views/mainContent/activeView$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t, type I18nLocale } from '@ardoq/i18n';
import { SaveAsViewpointErrorCode } from './saveAsViewpoint$/consts';

const translateErrorCode = (
  i18nLocale: I18nLocale,
  errorCode: SaveAsViewpointErrorCode | undefined
): string | undefined => {
  if (!errorCode) return undefined;
  switch (errorCode) {
    case SaveAsViewpointErrorCode.SAVE_BUTTON_NEEDS_NAME:
      return t(
        i18nLocale,
        'appLayout:saveAsViewpointErrors.saveButtonNeedsName',
        'Your viewpoint needs a name before it can be saved'
      );
    case SaveAsViewpointErrorCode.UPDATE_BUTTON_NEEDS_NAME:
      return t(
        i18nLocale,
        'appLayout:saveAsViewpointErrors.updateButtonNeedsName',
        'Your viewpoint needs a name before it can be updated'
      );
    case SaveAsViewpointErrorCode.VIEWPOINT_NAME_REQUIRED:
      return t(
        i18nLocale,
        'appLayout:saveAsViewpointErrors.viewpointNameRequired',
        'Please enter a Viewpoint name'
      );
    case SaveAsViewpointErrorCode.NO_PERMISSION_TO_MODIFY:
      return t(
        i18nLocale,
        'appLayout:saveAsViewpointErrors.noPermissionToModify',
        'You do not have permission to modify this viewpoint'
      );
    case SaveAsViewpointErrorCode.NO_PERMISSION_TO_CREATE:
      return t(
        i18nLocale,
        'appLayout:saveAsViewpointErrors.noPermissionToCreate',
        'You do not have permission to create a viewpoint'
      );
    default:
      errorCode satisfies never;
  }
};

const onNameFieldChange = (value: string) =>
  dispatchAction(updateSaveAsViewpointState({ viewpointName: value }));
const onDescriptionFieldChange = (value: string) =>
  dispatchAction(
    updateSaveAsViewpointState({
      viewpointDescription: value,
    })
  );
const onLocationChange = (folderId: ArdoqId | null) =>
  dispatchAction(updateSaveAsViewpointState({ folderId }));

type SaveAsViewpointProps = SaveAsViewpointState & {
  commands: SaveAsViewpointCommands;
  i18nLocale: I18nLocale;
};

const SaveAsViewpointBase = ({
  errors,
  viewpointName,
  viewpointDescription,
  folderId,
  mainViewName,
  appliedSettings,
  commands,
  i18nLocale,
}: SaveAsViewpointProps) => {
  useEffectOnce(() => dispatchAction(editorOpened()));
  const [isNameFieldFocused, setIsNameFieldFocused] = useState(false);

  const nameFieldErrorMessage = isNameFieldFocused
    ? undefined
    : translateErrorCode(i18nLocale, errors.viewpointName);

  const isExistingViewpoint = Boolean(
    appliedSettings.viewpointData?.traversalId
  );

  const primaryButtonError = translateErrorCode(
    i18nLocale,
    isExistingViewpoint ? errors.updateButton : errors.saveButton
  );
  const saveAsNewError = translateErrorCode(i18nLocale, errors.saveButton);

  return (
    <PanelContainer>
      <PanelHeader>
        <Icon iconName={IconName.VIEWPOINT} />
        <span>
          {t(i18nLocale, 'appLayout:saveAsViewpointTitle', 'Save as Viewpoint')}
        </span>
      </PanelHeader>
      <SectionsContainer>
        <InputsSection>
          <TextAreaWithCounter
            autoFocus
            id="save-as-viewpoint-name-input"
            label={t(
              i18nLocale,
              'appLayout:viewpointNameLabel',
              'Viewpoint name'
            )}
            helperText={t(
              i18nLocale,
              'appLayout:viewpointNameHelper',
              'Use a short name that describes the data others can get with this viewpoint'
            )}
            errorMessage={nameFieldErrorMessage}
            value={viewpointName}
            onValueChange={onNameFieldChange}
            characterLimit={60}
            characterLimitWarning={t(
              i18nLocale,
              'appLayout:viewpointNameCharLimitWarning',
              'Long names may not be fully displayed'
            )}
            onFocus={() => setIsNameFieldFocused(true)}
            onBlur={() => {
              setIsNameFieldFocused(false);
              // trigger validation when field loses focus
              dispatchAction(updateSaveAsViewpointState({}));
            }}
          />
          <LocationSelect folderId={folderId} onChange={onLocationChange} />
          <TextAreaWithCounter
            id="save-as-viewpoint-description-input"
            label={t(
              i18nLocale,
              'appLayout:viewpointDescriptionLabel',
              'Viewpoint description'
            )}
            placeholder={t(
              i18nLocale,
              'appLayout:viewpointDescriptionPlaceholder',
              'What information or questions can this viewpoint answer?'
            )}
            value={viewpointDescription}
            onValueChange={onDescriptionFieldChange}
            minRows={3}
            characterLimit={150}
            characterLimitWarning={t(
              i18nLocale,
              'appLayout:viewpointDescriptionCharLimitWarning',
              'Long descriptions may not be fully displayed'
            )}
            indicator="optional"
          />
          <StyledField
            label={t(i18nLocale, 'appLayout:viewStyleLabel', 'View style')}
          >
            <ReadOnlyStyle>{mainViewName}</ReadOnlyStyle>
          </StyledField>
        </InputsSection>
        <AppliedSettings
          appliedSettings={appliedSettings}
          i18nLocale={i18nLocale}
        />
      </SectionsContainer>
      <PanelFooter padding="medium" justify="end">
        <GhostButton
          onClick={() => {
            dispatchAction(hideViewSidePanel());
            dispatchAction(resetSaveAsViewpoint());
          }}
        >
          {t(i18nLocale, 'appLayout:cancel', 'Cancel')}
        </GhostButton>
        {isExistingViewpoint ? (
          <WithPopover content={saveAsNewError}>
            <Button
              buttonType={ButtonType.SECONDARY}
              isDisabled={Boolean(errors.saveButton)}
              onClick={commands.saveAsNew}
            >
              {t(i18nLocale, 'appLayout:saveAsNew', 'Save as new')}
            </Button>
          </WithPopover>
        ) : null}
        <WithPopover content={primaryButtonError}>
          <PrimaryButton
            isDisabled={Boolean(primaryButtonError)}
            onClick={
              isExistingViewpoint ? commands.updateExisting : commands.saveAsNew
            }
          >
            {isExistingViewpoint
              ? t(i18nLocale, 'appLayout:update', 'Update')
              : t(i18nLocale, 'appLayout:saveViewpoint', 'Save Viewpoint')}
          </PrimaryButton>
        </WithPopover>
      </PanelFooter>
    </PanelContainer>
  );
};

type SaveAsViewpointCommands = {
  saveAsNew: () => void;
  updateExisting: () => void;
};

const viewModel$ = combineLatest({
  viewpointState: saveAsViewpoint$,
  loadedState: loadedState$,
  i18nLocale: i18nLocale$,
  traversalState: traversalState$,
  activeView: activeViewState$,
}).pipe(
  map(
    ({
      viewpointState,
      loadedState: { loadedStates: loadedState },
      i18nLocale,
      traversalState,
      activeView,
    }) => {
      const traversalId =
        viewpointState.appliedSettings.viewpointData?.traversalId;
      const existingViewpoint = traversalId
        ? (traversalState.byId[traversalId] ?? null)
        : null;
      const viewSettings = activeView.mainViewSettings;
      return {
        ...viewpointState,
        i18nLocale,
        commands: {
          saveAsNew: () =>
            saveAsNew({ viewpointState, loadedState, viewSettings }),
          updateExisting: () =>
            updateExisting({
              viewpointState,
              loadedState,
              existingViewpoint,
              viewSettings,
            }),
        },
      };
    }
  )
);

const createViewpointAttributes = ({
  viewpointState,
  viewSettings,
  viewName,
}: {
  viewpointState: SaveAsViewpointState;
  viewSettings: Record<string, any>;
  viewName: ViewIds;
}) => {
  const {
    viewpointName: name,
    viewpointDescription: description,
    appliedSettings: { viewpointData },
  } = viewpointState;
  if (!name || !viewName || !viewpointData) {
    return null;
  }
  return {
    name,
    description,
    viewName,
    startType: viewpointData.startType,
    paths: viewpointData.paths,
    groupBys: viewpointData.groupBys,
    conditionalFormatting: viewpointData.conditionalFormatting,
    filters: viewpointData.filters,
    pathMatching: viewpointData.pathMatching,
    pathCollapsingRules: viewpointData.pathCollapsingRules,
    startSetExpansion: viewpointData.startSetExpansion ?? null,
    viewSettings: getViewSettingsToSave(viewSettings, viewName),
  };
};

const moveViewpointToFolder = (
  viewpointId: ArdoqId,
  folderId: ArdoqId | null
) =>
  dispatchAction(
    moveAssets({
      folderId,
      movedAssets: [{ _id: viewpointId, type: AssetType.TRAVERSAL }],
    })
  );

const saveAsNew = async ({
  viewpointState,
  loadedState,
  viewSettings,
}: {
  viewpointState: SaveAsViewpointState;
  loadedState: LoadedState[];
  viewSettings: Record<string, any>;
}) => {
  const attributes = createViewpointAttributes({
    viewpointState,
    viewSettings,
    viewName: viewpointState.mainViewId,
  });
  if (!attributes || !isSingleTraversalState(loadedState)) {
    return null;
  }

  const response = await dispatchActionAndWaitForResponse(
    createViewpoint(attributes),
    createViewpointSuccess,
    createViewpointFailure
  );
  if (!response.payload) {
    return null;
  }

  if (viewpointState.folderId) {
    moveViewpointToFolder(response.payload, viewpointState.folderId);
  }

  trackSavingViewpoint(
    getSaveViewpointTrackingData({
      appliedSettings: viewpointState.appliedSettings,
      viewName: viewpointState.mainViewId,
      saveAsNew: true,
      viewSettings,
    })
  );

  dispatchAction(
    setTraversalId({
      id: response.payload,
      loadedStateHash: loadedStateOperations.stateToHash(loadedState[0]),
    })
  );
  dispatchAction(hideViewSidePanel());
  dispatchAction(resetSaveAsViewpoint());
};

const updateExisting = async ({
  viewpointState,
  loadedState,
  existingViewpoint,
  viewSettings,
}: {
  viewpointState: SaveAsViewpointState;
  loadedState: LoadedState[];
  existingViewpoint: APIViewpointAttributes | null;
  viewSettings: Record<string, any>;
}) => {
  if (!existingViewpoint) {
    return null;
  }

  const attributes = createViewpointAttributes({
    viewpointState,
    viewSettings,
    viewName: toUpdatedViewpointViewId(
      viewpointState.mainViewId,
      existingViewpoint.viewName
    ),
  });
  if (!attributes || !isSingleTraversalState(loadedState)) {
    return null;
  }
  /**
   * dispatchActionAndWaitForResponse is used because setTraversalId needs to be dispatched after updateViewpointSuccess
   * has finished, otherwise the id will not have had time to be updated in the store. This can hopefully be removed when
   * we are no longer using Backbone
   */
  const response = await dispatchActionAndWaitForResponse(
    updateViewpoint({
      ...attributes,
      _id: existingViewpoint._id,
      _version: existingViewpoint._version,
    }),
    updateViewpointSuccess,
    updateViewpointFailure
  );
  if (!isActionOfType(updateViewpointSuccess, response)) {
    return null;
  }

  if (viewpointState.folderId !== viewpointState.originalFolderId) {
    moveViewpointToFolder(existingViewpoint._id, viewpointState.folderId);
  }

  trackSavingViewpoint(
    getSaveViewpointTrackingData({
      appliedSettings: viewpointState.appliedSettings,
      viewName: viewpointState.mainViewId,
      saveAsNew: false,
      viewSettings,
    })
  );

  dispatchAction(
    setTraversalId({
      id: existingViewpoint._id,
      loadedStateHash: loadedStateOperations.stateToHash(loadedState[0]),
    })
  );
  dispatchAction(hideViewSidePanel());
  dispatchAction(resetSaveAsViewpoint());
};

export const SaveAsViewpoint = connect(SaveAsViewpointBase, viewModel$);
